import { quickAddParse, todayISO, type QuickAddResult } from '@keel/core';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeftRight, CalendarDays, CircleHelp, Hash, Sparkles, Tag, Wallet } from 'lucide-react';
import { forwardRef, useMemo, useState, type ReactNode } from 'react';
import { errorMessage, useTRPC } from '@/api/query';
import {
  Badge, Button, IconButton, Input, Kbd, MoneyText, Popover, PopoverContent, PopoverTrigger, Select, toast, toastUndo,
} from '@/components/ui';
import { useDates } from '@/lib/dates';
import { useLedger, useRefresh } from './data';

export interface QuickAddPreview {
  readonly parsed: QuickAddResult | null;
  readonly error: string | null;
}

/** Live parse with core `quickAddParse` — the same parser the server runs, so the preview is what gets saved. */
export const useQuickAddPreview = (text: string, defaultAccountId: string | undefined): QuickAddPreview => {
  const { categories, payees, openAccounts, tags } = useLedger();
  return useMemo(() => {
    if (!text.trim()) return { parsed: null, error: null };
    try {
      return {
        parsed: quickAddParse(text, { today: todayISO(), categories, payees, accounts: openAccounts, tags, defaultAccountId }),
        error: null,
      };
    } catch (e) {
      return { parsed: null, error: errorMessage(e) };
    }
  }, [text, categories, payees, openAccounts, tags, defaultAccountId]);
};

const Chip = ({ icon, children, label }: { readonly icon: ReactNode; readonly children: ReactNode; readonly label: string }) => (
  <li className="inline-flex items-center gap-1 rounded-full bg-surface-sunken px-2.5 py-1 text-xs text-text [&_svg]:size-3.5 [&_svg]:text-text-muted">
    {icon}
    <span className="sr-only">{label}: </span>
    {children}
  </li>
);

const SYNTAX: readonly [string, string][] = [
  ['coffee 4.50', 'Spend $4.50 on coffee today'],
  ['salary +3200 friday', 'Money in, dated last Friday'],
  ['groceries 86.20 yesterday #weekly', 'Category, date and a tag'],
  ['lunch 18 @visa', 'Pay from the account named Visa'],
  ['transfer 500 to Savings', 'Move money between accounts'],
  ['rent 1950 1/11', 'A specific date (day first)'],
];

export interface QuickAddBarProps {
  /** Account the entry goes to unless the text names one with `@`. */
  readonly accountId?: string;
  /** Hide the account picker (the register is locked to one account). */
  readonly lockAccount?: boolean;
  readonly onAdded?: (id: string) => void;
}

/**
 * Natural-language entry ("coffee 4.50 yesterday #cafe"): a live preview of what will be saved, a syntax
 * popover, and `transactions.quickAdd` on Enter with an Undo toast.
 */
export const QuickAddBar = forwardRef<HTMLInputElement, QuickAddBarProps>(({ accountId: lockedAccountId, lockAccount, onAdded }, ref) => {
  const trpc = useTRPC();
  const refresh = useRefresh();
  const dates = useDates();
  const { entityId, openAccounts, categoryById, payeeById, accountById, canEdit } = useLedger();
  const [text, setText] = useState('');
  const [chosenAccount, setChosenAccount] = useState<string | undefined>(undefined);
  const accountId = lockedAccountId ?? chosenAccount ?? openAccounts[0]?.id;
  const { parsed, error } = useQuickAddPreview(text, accountId);
  const add = useMutation(trpc.transactions.quickAdd.mutationOptions());
  const remove = useMutation(trpc.transactions.remove.mutationOptions());

  if (!canEdit || !entityId) return null;
  const amount = parsed?.amountCents;
  const ready = Boolean(accountId && amount !== undefined && !error);
  const category = parsed?.splits?.[0]?.categoryId ? categoryById.get(parsed.splits[0].categoryId) : undefined;
  const payee = parsed?.payeeId ? payeeById.get(parsed.payeeId) : undefined;
  const account = parsed?.accountId ? accountById.get(parsed.accountId) : undefined;
  const to = parsed?.transferAccountId ? accountById.get(parsed.transferAccountId) : undefined;

  const submit = () => {
    if (!ready || !accountId) return;
    add.mutate(
      { entityId, accountId, text },
      {
        onSuccess: (t) => {
          setText('');
          // ready for the next one (the Add button took focus on a tap)
          document.getElementById('quick-add')?.focus();
          void refresh();
          onAdded?.(t.id);
          toastUndo(`Added ${t.payeeRaw || 'transaction'}`, () =>
            remove.mutate({ id: t.id }, { onSuccess: () => void refresh() }),
          );
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    );
  };

  return (
    <div className="rounded-lg border border-border bg-surface p-3 shadow-sm" data-tour="quick-add">
      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-center"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label htmlFor="quick-add" className="sr-only">
          Quick add a transaction
        </label>
        <Input
          ref={ref}
          id="quick-add"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setText('');
          }}
          placeholder='Quick add, e.g. "coffee 4.50 yesterday #cafe"'
          autoComplete="off"
          aria-describedby="quick-add-preview"
          start={<Sparkles aria-hidden />}
          className="sm:flex-1"
        />
        {lockAccount ? null : (
          <Select
            aria-label="Default account for quick add"
            value={accountId ?? ''}
            onValueChange={setChosenAccount}
            options={openAccounts.map((a) => ({ value: a.id, label: a.name }))}
            className="sm:w-48"
            placeholder="Account"
          />
        )}
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <IconButton aria-label="Quick add examples" icon={<CircleHelp aria-hidden />} />
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80">
              <p className="mb-2 font-medium">Type it like you'd say it</p>
              <dl className="flex flex-col gap-2">
                {SYNTAX.map(([ex, what]) => (
                  <div key={ex}>
                    <dt>
                      <button type="button" className="font-medium text-primary underline-offset-4 hover:underline" onClick={() => setText(ex)}>
                        {ex}
                      </button>
                    </dt>
                    <dd className="text-xs text-text-muted">{what}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-3 text-xs text-text-muted">
                Amounts are money out unless you add <Kbd className="coarse:!inline-flex">+</Kbd> or pick an income category.
              </p>
            </PopoverContent>
          </Popover>
          <Button type="submit" disabled={!ready} loading={add.isPending} className="flex-1 sm:flex-none">
            Add
          </Button>
        </div>
      </form>
      <div id="quick-add-preview" aria-live="polite" className="min-h-0">
        {error ? <p className="mt-2 text-sm text-negative">{error}</p> : null}
        {parsed ? (
          <ul className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="Will be saved as">
            {amount !== undefined ? (
              <Chip icon={null} label="Amount">
                <MoneyText cents={amount} sign="always" tone="auto" className="font-semibold" />
              </Chip>
            ) : (
              <li>
                <Badge tone="warning">Add an amount</Badge>
              </li>
            )}
            {parsed.date ? (
              <Chip icon={<CalendarDays aria-hidden />} label="Date">
                {parsed.date === dates.today ? 'Today' : dates.format(parsed.date, 'weekday')}
              </Chip>
            ) : null}
            {parsed.isTransfer ? (
              <Chip icon={<ArrowLeftRight aria-hidden />} label="Transfer">
                {account?.name ?? 'This account'} to {to?.name ?? 'choose an account'}
              </Chip>
            ) : (
              <>
                {parsed.payeeRaw ? (
                  <Chip icon={null} label="Payee">
                    {payee?.name ?? parsed.payeeRaw}
                  </Chip>
                ) : null}
                <Chip icon={<Tag aria-hidden />} label="Category">
                  {category?.name ?? 'Uncategorised'}
                </Chip>
                {account && account.id !== accountId ? (
                  <Chip icon={<Wallet aria-hidden />} label="Account">
                    {account.name}
                  </Chip>
                ) : null}
              </>
            )}
            {parsed.tagNames.map((t) => (
              <Chip key={t} icon={<Hash aria-hidden />} label="Tag">
                {t}
              </Chip>
            ))}
            {ready ? (
              <li className="ml-auto hidden text-xs text-text-muted sm:block coarse:!hidden">
                Press <Kbd>Enter</Kbd> to add
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </div>
  );
});
QuickAddBar.displayName = 'QuickAddBar';
